import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FileManager from "@/components/files/FileManager";
import EmployeeAvatar from "@/components/communications/EmployeeAvatar";
import ProfilePhotoUploader from "@/components/communications/ProfilePhotoUploader";
import { StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft,
  BadgeDollarSign,
  Building2,
  CalendarCheck2,
  ContactRound,
  Landmark,
  Pencil,
  Plus,
  ShieldCheck,
} from "lucide-react";

const TABS = [
  ["personal", "Personal"],
  ["job", "Job"],
  ["salary", "Salary"],
  ["documents", "Documents"],
  ["bank", "Bank"],
  ["emergency", "Emergency Contact"],
  ["assets", "Assets"],
  ["timeline", "Timeline"],
];
const WORK_AUTHORIZATION_OPTIONS = [
  "Citizen",
  "Permanent Resident",
  "Work Visa",
  "Student Visa",
  "EAD",
  "Other",
];
const BLOOD_GROUP_OPTIONS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

export default function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [history, setHistory] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [sensitiveOpen, setSensitiveOpen] = useState(false);
  const [assetOpen, setAssetOpen] = useState(false);
  const [personalDetailsOpen, setPersonalDetailsOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const canManage = can("hrms:manage") || user?.role === "super_admin";
  const canSensitive = can("hrms:view_sensitive") || can("hrms:payroll");
  const canUpdatePhoto = canManage || employee?.user_id === user?.id;
  const canUpdatePersonalDetails =
    employee?.user_id === user?.id || user?.role === "super_admin";

  const load = useCallback(async () => {
    try {
      const [{ data: profile }, { data: audit }] = await Promise.all([
        api.get(`/hrms/employees/${id}`),
        api.get(`/hrms/history/employee/${id}`),
      ]);
      setEmployee(profile);
      setHistory(audit.items || []);
    } catch (error) {
      toast.error(apiErr(error));
      navigate("/hrms");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const profilePayload = useCallback(
    (changes = {}) => ({
      personal: employee?.personal || {},
      contact_information: employee?.contact_information || {},
      emergency_contact: employee?.emergency_contact || {},
      designation: employee?.designation || "",
      department: employee?.department || "",
      location: employee?.location || "",
      employment_type: employee?.employment_type || "full_time",
      joining_date: employee?.joining_date || null,
      probation_end: employee?.probation_end || null,
      reporting_manager_id: employee?.reporting_manager_id || null,
      team_id: employee?.team_id || null,
      shift_id: employee?.shift_id || null,
      assets: employee?.assets || [],
      ...changes,
    }),
    [employee],
  );

  const updateProfile = async (changes) => {
    setSaving(true);
    try {
      const { data } = await api.put(
        `/hrms/employees/${id}`,
        profilePayload(changes),
      );
      setEmployee(data);
      setEditOpen(false);
      setAssetOpen(false);
      toast.success("Employee profile updated");
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const updateSensitive = async (value) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/hrms/employees/${id}/sensitive`, value);
      setEmployee(data);
      setSensitiveOpen(false);
      toast.success("Encrypted bank and tax details updated");
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const updatePersonalDetails = async (value) => {
    setSaving(true);
    try {
      const { data } = await api.put(
        `/hrms/employees/${id}/personal-details`,
        value,
      );
      setEmployee(data);
      setPersonalDetailsOpen(false);
      toast.success("Personal details updated");
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const updateLifecycle = async (kind, itemId) => {
    const lifecycle = employee?.[kind] || {};
    const checklist = (lifecycle.checklist || []).map((item) =>
      item.id === itemId
        ? {
            ...item,
            completed: !item.completed,
            completed_at: item.completed ? null : new Date().toISOString(),
          }
        : item,
    );
    setSaving(true);
    try {
      await api.put(`/hrms/employees/${id}/${kind}`, {
        status: lifecycle.status || "in_progress",
        checklist,
        notes: lifecycle.notes || "",
      });
      toast.success("Lifecycle checklist updated");
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const salary = useMemo(() => employee?.salary || {}, [employee]);
  if (!employee) {
    return (
      <Card className="p-6" data-testid="employee-profile-loading">
        Loading employee profile…
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="employee-profile-page">
      <Button
        variant="ghost"
        className="gap-1.5 px-0"
        onClick={() => navigate("/hrms")}
        data-testid="employee-profile-back-button"
      >
        <ArrowLeft className="h-4 w-4" /> Back to People & HRMS
      </Button>

      <Card
        className="border-l-4 border-l-[#255058] p-6"
        data-testid="employee-profile-header"
      >
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="flex items-start gap-4">
            <div>
              <EmployeeAvatar
                className="h-16 w-16"
                fileId={employee.profile_photo_id}
                name={employee.name || employee.employee_id}
                testId="employee-profile-avatar"
              />
              {canUpdatePhoto && (
                <div className="mt-2">
                  <ProfilePhotoUploader
                    employeeId={employee.id}
                    onSaved={(profile_photo_id) =>
                      setEmployee((current) => ({
                        ...current,
                        profile_photo_id,
                      }))
                    }
                  />
                </div>
              )}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-display text-3xl font-bold">
                  {employee.name || employee.employee_id}
                </h1>
                <StatusBadge value={employee.employment_status || "active"} />
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {employee.employee_id} ·{" "}
                {employee.designation || "Designation pending"}
              </p>
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                <span>
                  {employee.company_email ||
                    employee.personal?.personal_email ||
                    "No email"}
                </span>
                <span>{employee.department || "Department pending"}</span>
                <span>{employee.location || "Location pending"}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canUpdatePersonalDetails && (
              <Button
                variant="outline"
                onClick={() => setPersonalDetailsOpen(true)}
                data-testid="employee-personal-details-edit-button"
              >
                <Pencil className="mr-1.5 h-4 w-4" /> Update personal details
              </Button>
            )}
            {canManage && (
              <Button
                onClick={() => setEditOpen(true)}
                className="bg-[#255058] text-white hover:bg-[#1c3d43]"
                data-testid="employee-profile-edit-button"
              >
                <Pencil className="mr-1.5 h-4 w-4" /> Edit HR profile
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Tabs defaultValue="personal" data-testid="employee-profile-tabs">
        <TabsList className="h-auto flex-wrap justify-start">
          {TABS.map(([value, label]) => (
            <TabsTrigger
              key={value}
              value={value}
              data-testid={`employee-profile-tab-${value}`}
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="personal" className="mt-5">
          <DetailGrid
            title="Personal information"
            icon={ContactRound}
            items={[
              ["Full name", employee.personal?.full_name || employee.name],
              [
                "Official work email",
                employee.official_work_email || employee.company_email,
              ],
              [
                "Personal email",
                employee.personal?.personal_email || employee.company_email,
              ],
              [
                "Phone",
                employee.personal?.phone || employee.contact_information?.phone,
              ],
              ["Date of birth", employee.personal?.date_of_birth],
              ["Address", employee.personal?.address],
              ["Work authorization", employee.personal?.work_authorization],
              ["Blood group", employee.personal?.blood_group],
            ]}
          />
        </TabsContent>

        <TabsContent value="job" className="mt-5">
          <div className="space-y-5">
            <DetailGrid
              title="Employment details"
              icon={Building2}
              items={[
                ["Designation", employee.designation],
                ["Department", employee.department],
                ["Employment type", formatLabel(employee.employment_type)],
                ["Joining date", employee.joining_date],
                ["Probation end", employee.probation_end],
                ["Team", employee.team_name],
                [
                  "Placement",
                  employee.placement_id ? "Linked ATS placement" : "Not linked",
                ],
              ]}
            />
            <LifecycleCard
              title="Onboarding"
              lifecycle={employee.onboarding}
              canManage={canManage}
              saving={saving}
              onToggle={(itemId) => updateLifecycle("onboarding", itemId)}
              testId="employee-onboarding"
            />
            <LifecycleCard
              title="Offboarding"
              lifecycle={employee.offboarding}
              canManage={canManage}
              saving={saving}
              onToggle={(itemId) => updateLifecycle("offboarding", itemId)}
              testId="employee-offboarding"
            />
          </div>
        </TabsContent>

        <TabsContent value="salary" className="mt-5">
          <DetailGrid
            title="Salary and payroll"
            icon={BadgeDollarSign}
            items={
              canSensitive
                ? [
                    [
                      "Base monthly salary",
                      money(salary.base_monthly ?? salary.base),
                    ],
                    [
                      "Overtime hourly rate",
                      money(salary.overtime_hourly_rate),
                    ],
                    ["Allowances", moneyTotal(employee.allowances)],
                    ["Deductions", moneyTotal(employee.deductions)],
                  ]
                : [["Access", "Restricted to payroll and HR administrators"]]
            }
          />
        </TabsContent>

        <TabsContent value="documents" className="mt-5">
          <FileManager
            recordType="employee"
            recordId={employee.id}
            canWrite={canManage || canSensitive}
            title="Employee documents"
          />
        </TabsContent>

        <TabsContent value="bank" className="mt-5">
          <Card className="p-5" data-testid="employee-bank-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 font-display text-lg font-semibold">
                  <Landmark className="h-5 w-5 text-[#255058]" /> Bank and tax
                  details
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Stored encrypted; only masked values are displayed.
                </p>
              </div>
              {canSensitive && (
                <Button
                  variant="outline"
                  onClick={() => setSensitiveOpen(true)}
                  data-testid="employee-sensitive-edit-button"
                >
                  <ShieldCheck className="mr-1.5 h-4 w-4" /> Update protected
                  details
                </Button>
              )}
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <MaskedGroup
                title="Bank"
                values={employee.sensitive_summary?.bank}
              />
              <MaskedGroup
                title="Tax"
                values={employee.sensitive_summary?.tax}
              />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="emergency" className="mt-5">
          <DetailGrid
            title="Emergency contact"
            icon={ContactRound}
            items={[
              ["Name", employee.emergency_contact?.name],
              ["Relationship", employee.emergency_contact?.relationship],
              ["Phone", employee.emergency_contact?.phone],
              ["Email", employee.emergency_contact?.email],
            ]}
          />
        </TabsContent>

        <TabsContent value="assets" className="mt-5">
          <AssetsCard
            assets={employee.assets || []}
            canManage={canManage}
            onAdd={() => setAssetOpen(true)}
          />
        </TabsContent>

        <TabsContent value="timeline" className="mt-5">
          <Card className="p-5" data-testid="employee-timeline-card">
            <div className="flex items-center gap-2 font-display text-lg font-semibold">
              <CalendarCheck2 className="h-5 w-5 text-[#255058]" /> Profile
              timeline
            </div>
            <div className="mt-4 space-y-3">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="border-l-2 border-[#255058]/30 pl-4"
                  data-testid={`employee-timeline-item-${item.id}`}
                >
                  <div className="font-medium">{formatLabel(item.action)}</div>
                  <div className="text-sm text-muted-foreground">
                    {item.user_name || "System"} ·{" "}
                    {formatDateTime(item.created_at)}
                  </div>
                  {item.reason && (
                    <div className="mt-1 text-sm">Reason: {item.reason}</div>
                  )}
                </div>
              ))}
              {!history.length && (
                <div className="text-sm text-muted-foreground">
                  No profile activity yet.
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <EmployeeEditDialog
        employee={employee}
        open={editOpen}
        saving={saving}
        isSuperAdmin={user?.role === "super_admin"}
        onClose={() => setEditOpen(false)}
        onSave={updateProfile}
      />
      <PersonalDetailsDialog
        employee={employee}
        isSuperAdmin={user?.role === "super_admin"}
        onClose={() => setPersonalDetailsOpen(false)}
        onSave={updatePersonalDetails}
        open={personalDetailsOpen}
        saving={saving}
      />
      <SensitiveDetailsDialog
        open={sensitiveOpen}
        saving={saving}
        isSuperAdmin={user?.role === "super_admin"}
        onClose={() => setSensitiveOpen(false)}
        onSave={updateSensitive}
      />
      <AssetDialog
        open={assetOpen}
        saving={saving}
        onClose={() => setAssetOpen(false)}
        onSave={(asset) =>
          updateProfile({ assets: [...(employee.assets || []), asset] })
        }
      />
    </div>
  );
}

function DetailGrid({ icon: Icon, items, title }) {
  return (
    <Card
      className="p-5"
      data-testid={`employee-detail-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-2 font-display text-lg font-semibold">
        <Icon className="h-5 w-5 text-[#255058]" /> {title}
      </div>
      <dl className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2">
        {items.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs font-semibold uppercase text-muted-foreground">
              {label}
            </dt>
            <dd className="mt-1 text-sm font-medium">{value || "—"}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function LifecycleCard({
  canManage,
  lifecycle,
  onToggle,
  saving,
  testId,
  title,
}) {
  const checklist = lifecycle?.checklist || [];
  return (
    <Card className="p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-lg font-semibold">{title}</div>
          <div className="text-sm text-muted-foreground">
            {lifecycle?.template_name || "Standard checklist"}
          </div>
        </div>
        <StatusBadge value={lifecycle?.status || "not_started"} />
      </div>
      <div className="mt-4 space-y-2">
        {checklist.map((item) => (
          <label
            key={item.id}
            className="flex items-center gap-3 rounded-md border border-border px-3 py-2 text-sm"
          >
            <input
              type="checkbox"
              checked={Boolean(item.completed)}
              disabled={!canManage || saving}
              onChange={() => onToggle(item.id)}
              data-testid={`${testId}-item-${item.id}`}
            />
            <span
              className={
                item.completed
                  ? "text-muted-foreground line-through"
                  : "font-medium"
              }
            >
              {item.label}
            </span>
          </label>
        ))}
        {!checklist.length && (
          <div className="text-sm text-muted-foreground">
            No checklist items.
          </div>
        )}
      </div>
    </Card>
  );
}

function MaskedGroup({ title, values = {} }) {
  return (
    <div
      className="rounded-md border border-border p-4"
      data-testid={`employee-masked-${title.toLowerCase()}`}
    >
      <div className="font-medium">{title}</div>
      <div className="mt-3 space-y-2 text-sm">
        {Object.entries(values).map(([key, value]) => (
          <div key={key} className="flex justify-between gap-4">
            <span className="text-muted-foreground">{formatLabel(key)}</span>
            <span className="font-medium">{value || "Not provided"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AssetsCard({ assets, canManage, onAdd }) {
  return (
    <Card className="p-5" data-testid="employee-assets-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="font-display text-lg font-semibold">
          Assigned assets
        </div>
        {canManage && (
          <Button
            size="sm"
            onClick={onAdd}
            data-testid="employee-add-asset-button"
          >
            <Plus className="mr-1.5 h-4 w-4" /> Add asset
          </Button>
        )}
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {assets.map((asset, index) => (
          <div
            key={`${asset.name}-${index}`}
            className="rounded-md border border-border p-4"
          >
            <div className="font-medium">{asset.name}</div>
            <div className="mt-1 text-sm text-muted-foreground">
              {asset.type || "Asset"} · {asset.asset_tag || "No tag"}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {asset.notes || "No notes"}
            </div>
          </div>
        ))}
        {!assets.length && (
          <div className="text-sm text-muted-foreground">
            No assets assigned.
          </div>
        )}
      </div>
    </Card>
  );
}

function PersonalDetailsDialog({
  employee,
  isSuperAdmin,
  onClose,
  onSave,
  open,
  saving,
}) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm({
      official_work_email:
        employee.official_work_email || employee.company_email || "",
      date_of_birth: employee.personal?.date_of_birth || "",
      work_authorization: employee.personal?.work_authorization || "",
      blood_group: employee.personal?.blood_group || "",
      reason: "",
    });
  }, [employee, open]);

  const change = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent data-testid="employee-personal-details-dialog">
        <DialogHeader>
          <DialogTitle>Update personal details</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Official work email">
            <Input
              type="email"
              value={form.official_work_email || ""}
              onChange={(event) => change("official_work_email", event.target.value)}
              data-testid="employee-personal-details-work-email"
            />
          </Field>
          <Field label="Date of birth">
            <Input
              type="date"
              value={form.date_of_birth || ""}
              onChange={(event) => change("date_of_birth", event.target.value)}
              data-testid="employee-personal-details-date-of-birth"
            />
          </Field>
          <Field label="Work authorization">
            <Select
              value={form.work_authorization || "none"}
              onValueChange={(value) => change("work_authorization", value === "none" ? "" : value)}
            >
              <SelectTrigger data-testid="employee-personal-details-work-authorization">
                <SelectValue placeholder="Select authorization" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not specified</SelectItem>
                {WORK_AUTHORIZATION_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Blood group">
            <Select
              value={form.blood_group || "none"}
              onValueChange={(value) => change("blood_group", value === "none" ? "" : value)}
            >
              <SelectTrigger data-testid="employee-personal-details-blood-group">
                <SelectValue placeholder="Select blood group" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not specified</SelectItem>
                {BLOOD_GROUP_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        {isSuperAdmin && (
          <Field label="Reason for Super Admin override">
            <Textarea
              value={form.reason || ""}
              onChange={(event) => change("reason", event.target.value)}
              data-testid="employee-personal-details-override-reason"
            />
          </Field>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="employee-personal-details-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSave(form)}
            disabled={saving || (isSuperAdmin && !form.reason?.trim())}
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
            data-testid="employee-personal-details-save-button"
          >
            Save personal details
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmployeeEditDialog({
  employee,
  isSuperAdmin,
  onClose,
  onSave,
  open,
  saving,
}) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (!open) return;
    setForm({
      full_name: employee.personal?.full_name || employee.name || "",
      personal_email:
        employee.personal?.personal_email || employee.company_email || "",
      phone:
        employee.personal?.phone || employee.contact_information?.phone || "",
      address: employee.personal?.address || "",
      designation: employee.designation || "",
      department: employee.department || "",
      location: employee.location || "",
      employment_type: employee.employment_type || "full_time",
      joining_date: employee.joining_date || "",
      probation_end: employee.probation_end || "",
      emergency_name: employee.emergency_contact?.name || "",
      emergency_relationship: employee.emergency_contact?.relationship || "",
      emergency_phone: employee.emergency_contact?.phone || "",
      emergency_email: employee.emergency_contact?.email || "",
      reason: "",
    });
  }, [employee, open]);
  const change = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        data-testid="employee-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle>Edit employee profile</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            ["Full name", "full_name", "employee-edit-full-name"],
            [
              "Personal email",
              "personal_email",
              "employee-edit-personal-email",
            ],
            ["Phone", "phone", "employee-edit-phone"],
            ["Address", "address", "employee-edit-address"],
            ["Designation", "designation", "employee-edit-designation"],
            ["Department", "department", "employee-edit-department"],
            ["Location", "location", "employee-edit-location"],
            [
              "Employment type",
              "employment_type",
              "employee-edit-employment-type",
            ],
            [
              "Emergency contact name",
              "emergency_name",
              "employee-edit-emergency-name",
            ],
            [
              "Emergency contact phone",
              "emergency_phone",
              "employee-edit-emergency-phone",
            ],
            [
              "Emergency relationship",
              "emergency_relationship",
              "employee-edit-emergency-relationship",
            ],
            [
              "Emergency email",
              "emergency_email",
              "employee-edit-emergency-email",
            ],
          ].map(([label, key, testId]) => (
            <Field key={key} label={label}>
              <Input
                value={form[key] || ""}
                onChange={(event) => change(key, event.target.value)}
                data-testid={testId}
              />
            </Field>
          ))}
          <Field label="Joining date">
            <Input
              type="date"
              value={form.joining_date || ""}
              onChange={(event) => change("joining_date", event.target.value)}
              data-testid="employee-edit-joining-date"
            />
          </Field>
          <Field label="Probation end">
            <Input
              type="date"
              value={form.probation_end || ""}
              onChange={(event) => change("probation_end", event.target.value)}
              data-testid="employee-edit-probation-end"
            />
          </Field>
        </div>
        {isSuperAdmin && (
          <Field label="Reason for Super Admin override">
            <Textarea
              value={form.reason || ""}
              onChange={(event) => change("reason", event.target.value)}
              data-testid="employee-edit-override-reason"
            />
          </Field>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="employee-edit-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSave(buildProfileFormPayload(form))}
            disabled={saving || (isSuperAdmin && !form.reason?.trim())}
            data-testid="employee-edit-save-button"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            Save profile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SensitiveDetailsDialog({
  isSuperAdmin,
  onClose,
  onSave,
  open,
  saving,
}) {
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm({
      bank_name: "",
      account_number: "",
      routing_number: "",
      tax_country: "",
      tax_id: "",
      reason: "",
    });
  }, [open]);
  const change = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent data-testid="employee-sensitive-dialog">
        <DialogHeader>
          <DialogTitle>Update protected bank and tax details</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          {[
            ["Bank name", "bank_name", "employee-bank-name-input"],
            [
              "Account or IBAN",
              "account_number",
              "employee-bank-account-input",
            ],
            ["Routing number", "routing_number", "employee-bank-routing-input"],
            ["Tax country", "tax_country", "employee-tax-country-input"],
            ["Tax ID", "tax_id", "employee-tax-id-input"],
          ].map(([label, key, testId]) => (
            <Field key={key} label={label}>
              <Input
                value={form[key] || ""}
                onChange={(event) => change(key, event.target.value)}
                data-testid={testId}
              />
            </Field>
          ))}
          {isSuperAdmin && (
            <Field label="Reason for Super Admin override">
              <Textarea
                value={form.reason || ""}
                onChange={(event) => change("reason", event.target.value)}
                data-testid="employee-sensitive-override-reason"
              />
            </Field>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="employee-sensitive-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSave(buildSensitivePayload(form))}
            disabled={saving || (isSuperAdmin && !form.reason?.trim())}
            data-testid="employee-sensitive-save-button"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            Save protected details
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssetDialog({ onClose, onSave, open, saving }) {
  const [asset, setAsset] = useState({
    name: "",
    type: "",
    asset_tag: "",
    notes: "",
  });
  useEffect(() => {
    setAsset({ name: "", type: "", asset_tag: "", notes: "" });
  }, [open]);
  const change = (key, value) => {
    setAsset((current) => ({ ...current, [key]: value }));
  };
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent data-testid="employee-asset-dialog">
        <DialogHeader>
          <DialogTitle>Assign asset</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Asset name">
            <Input
              value={asset.name}
              onChange={(event) => change("name", event.target.value)}
              data-testid="employee-asset-name-input"
            />
          </Field>
          <Field label="Type">
            <Input
              value={asset.type}
              onChange={(event) => change("type", event.target.value)}
              data-testid="employee-asset-type-input"
            />
          </Field>
          <Field label="Asset tag">
            <Input
              value={asset.asset_tag}
              onChange={(event) => change("asset_tag", event.target.value)}
              data-testid="employee-asset-tag-input"
            />
          </Field>
          <Field label="Notes">
            <Textarea
              value={asset.notes}
              onChange={(event) => change("notes", event.target.value)}
              data-testid="employee-asset-notes-input"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="employee-asset-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSave(asset)}
            disabled={saving || !asset.name.trim()}
            data-testid="employee-asset-save-button"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            Assign asset
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ children, label }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function buildProfileFormPayload(form) {
  return {
    personal: {
      full_name: form.full_name,
      personal_email: form.personal_email,
      phone: form.phone,
      address: form.address,
    },
    contact_information: { email: form.personal_email, phone: form.phone },
    emergency_contact: {
      name: form.emergency_name,
      relationship: form.emergency_relationship,
      phone: form.emergency_phone,
      email: form.emergency_email,
    },
    designation: form.designation,
    department: form.department,
    location: form.location,
    employment_type: form.employment_type,
    joining_date: form.joining_date || null,
    probation_end: form.probation_end || null,
    reason: form.reason,
  };
}

function buildSensitivePayload(form) {
  return {
    bank_details: {
      bank_name: form.bank_name,
      account_number: form.account_number,
      routing_number: form.routing_number,
    },
    tax_details: { tax_country: form.tax_country, tax_id: form.tax_id },
    reason: form.reason,
  };
}

function formatLabel(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString() : "—";
}

function money(value) {
  if (value === undefined || value === null || value === "") return "—";
  return `$${Number(value).toLocaleString()}`;
}

function moneyTotal(rows = []) {
  return money(rows.reduce((total, row) => total + Number(row.amount || 0), 0));
}
